import { decode, ExtensionCodec, encode } from '@msgpack/msgpack';
// Custom extension codec for handling Uint16Array (tile data)
const extensionCodec = new ExtensionCodec();
// Extension type 1: Uint16Array
extensionCodec.register({
    type: 1,
    encode: (object) => {
        if (object instanceof Uint16Array) {
            return new Uint8Array(object.buffer, object.byteOffset, object.byteLength);
        }
        return null;
    },
    decode: (data) => {
        const buffer = new ArrayBuffer(data.length);
        const view = new Uint8Array(buffer);
        view.set(data);
        return new Uint16Array(buffer);
    },
});
export const serializeMessage = (message) => {
    return encode(message, { extensionCodec });
};
export const deserializeMessage = (data) => {
    return decode(data, { extensionCodec });
};
// Required fields - always included in base delta for new entities
const REQUIRED_FIELDS = {
    x: { compare: 'threshold', threshold: 0.01 },
    y: { compare: 'threshold', threshold: 0.01 },
    vx: { compare: 'threshold', threshold: 0.01 },
    vy: { compare: 'threshold', threshold: 0.01 },
    health: { compare: 'exact' },
    maxHealth: { compare: 'exact' },
    state: { compare: 'exact' },
};
// Optional fields - only included if defined, simple comparison
// To add a new field: just add one line here!
const OPTIONAL_FIELDS = {
    hunger: { compare: 'exact' },
    rest: { compare: 'exact' },
    mood: { compare: 'exact' },
    combatTarget: { compare: 'exact', defaultValue: 0 },
    combatState: { compare: 'exact', defaultValue: 0 },
    isDowned: { compare: 'exact', defaultValue: 0 },
    isUnconscious: { compare: 'exact', defaultValue: 0 },
    carriedByNetworkId: { compare: 'exact', defaultValue: 0 },
    carryingNetworkId: { compare: 'exact', defaultValue: 0 },
    restingInBedNetworkId: { compare: 'exact', defaultValue: 0 },
    equippedWeapon: { compare: 'exact', defaultValue: 0 },
    equippedPickaxe: { compare: 'exact', defaultValue: 0 },
    equippedAxe: { compare: 'exact', defaultValue: 0 },
};
// Path-related fields - grouped together (change detection is linked)
const PATH_FIELDS = ['path', 'pathIndex', 'targetX', 'targetY'];
// Pre-cached keys/entries to avoid allocation on every delta computation
const REQUIRED_KEYS = Object.keys(REQUIRED_FIELDS);
const REQUIRED_ENTRIES = Object.entries(REQUIRED_FIELDS);
const OPTIONAL_KEYS = Object.keys(OPTIONAL_FIELDS);
const OPTIONAL_ENTRIES = Object.entries(OPTIONAL_FIELDS);
// Helper: Check if field value changed based on comparison type
const hasFieldChanged = ({ prev, curr, def, }) => {
    if (curr === undefined) {
        if (prev === undefined) {
            return false;
        }
        if (def.defaultValue === undefined) {
            return false;
        }
        return prev !== def.defaultValue;
    }
    if (prev === undefined) {
        return true;
    }
    if (def.compare === 'threshold' && def.threshold !== undefined) {
        return Math.abs(curr - prev) > def.threshold;
    }
    return curr !== prev;
};
// ============================================================================
// Delta Compression
// ============================================================================
// Build delta for a new entity (no previous state)
const buildNewEntityDelta = ({ current, delta, }) => {
    const curr = current;
    const d = delta;
    for (const key of REQUIRED_KEYS) {
        d[key] = curr[key];
    }
    for (const key of OPTIONAL_KEYS) {
        if (curr[key] !== undefined) {
            d[key] = curr[key];
        }
    }
    for (const key of PATH_FIELDS) {
        if (curr[key] !== undefined) {
            d[key] = curr[key];
        }
    }
    if (current.task !== undefined) {
        delta.task = current.task;
    }
    return delta;
};
// Check if path-related fields have changed
const hasPathFieldsChanged = ({ previous, current, }) => {
    const prevPathLen = previous.path?.length ?? 0;
    const currPathLen = current.path?.length ?? 0;
    return (prevPathLen !== currPathLen ||
        current.pathIndex !== previous.pathIndex ||
        current.targetX !== previous.targetX ||
        current.targetY !== previous.targetY);
};
// Check if task has changed (with 1% progress threshold)
const hasTaskChanged = ({ prevTask, currTask, }) => {
    if (prevTask === undefined && currTask !== undefined) {
        return true;
    }
    if (prevTask !== undefined && currTask === undefined) {
        return true;
    }
    if (prevTask && currTask) {
        return (prevTask.taskType !== currTask.taskType ||
            prevTask.targetEntity !== currTask.targetEntity ||
            Math.abs(prevTask.progress - currTask.progress) > 0.01);
    }
    return false;
};
// Process required and optional field changes
const processFieldChanges = ({ prev, curr, delta, }) => {
    let hasChanges = false;
    for (const [key, def] of REQUIRED_ENTRIES) {
        if (hasFieldChanged({ prev: prev[key], curr: curr[key], def })) {
            delta[key] = curr[key];
            hasChanges = true;
        }
    }
    for (const [key, def] of OPTIONAL_ENTRIES) {
        if (hasFieldChanged({ prev: prev[key], curr: curr[key], def })) {
            delta[key] = curr[key] ?? def.defaultValue;
            hasChanges = true;
        }
    }
    return hasChanges;
};
export const computeEntityDelta = ({ previous, current, }) => {
    const delta = { networkId: current.networkId };
    if (!previous) {
        return buildNewEntityDelta({ current, delta });
    }
    const prev = previous;
    const curr = current;
    const d = delta;
    let hasChanges = processFieldChanges({ prev, curr, delta: d });
    if (hasPathFieldsChanged({ previous, current })) {
        for (const key of PATH_FIELDS) {
            if (curr[key] !== undefined) {
                d[key] = curr[key];
            }
        }
        hasChanges = true;
    }
    if (hasTaskChanged({ prevTask: previous.task, currTask: current.task })) {
        // Send task: null when task is cleared, otherwise send the new task
        delta.task = current.task ?? null;
        hasChanges = true;
    }
    return hasChanges ? delta : null;
};
// ============================================================================
// Delta Application
// ============================================================================
export const applyEntityDelta = ({ entity, delta, }) => {
    const prev = entity;
    const d = delta;
    // Start with immutable fields
    const result = {
        networkId: entity.networkId,
        entityType: entity.entityType,
        ownerId: entity.ownerId,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        health: 0,
        maxHealth: 0,
        state: 0,
    };
    const r = result;
    // Required fields - delta overrides entity
    for (const key of REQUIRED_KEYS) {
        r[key] = d[key] ?? prev[key];
    }
    // Optional fields - delta overrides entity, only set if defined
    for (const key of OPTIONAL_KEYS) {
        const value = d[key] !== undefined ? d[key] : prev[key];
        if (value !== undefined) {
            r[key] = value;
        }
    }
    // Path fields - delta overrides entity, only set if defined
    for (const key of PATH_FIELDS) {
        const value = d[key] !== undefined ? d[key] : prev[key];
        if (value !== undefined) {
            r[key] = value;
        }
    }
    // Task - delta overrides entity, only set if defined
    const task = delta.task !== undefined ? delta.task : entity.task;
    if (task !== undefined) {
        result.task = task;
    }
    return result;
};
// Optimized entity snapshot serialization
// Layout per entity: networkId(4) + x(4) + y(4) + vx(4) + vy(4) + health(2) + state(1) = 23 bytes
export const ENTITY_SNAPSHOT_BYTE_SIZE = 23;
export const serializeEntitySnapshots = ({ snapshots, }) => {
    const buffer = new ArrayBuffer(snapshots.length * ENTITY_SNAPSHOT_BYTE_SIZE);
    const view = new DataView(buffer);
    let offset = 0;
    for (const snapshot of snapshots) {
        view.setUint32(offset, snapshot.networkId, true);
        offset += 4;
        view.setFloat32(offset, snapshot.x, true);
        offset += 4;
        view.setFloat32(offset, snapshot.y, true);
        offset += 4;
        view.setFloat32(offset, snapshot.vx, true);
        offset += 4;
        view.setFloat32(offset, snapshot.vy, true);
        offset += 4;
        view.setUint16(offset, Math.round(snapshot.health), true);
        offset += 2;
        view.setUint8(offset, snapshot.state);
        offset += 1;
    }
    return new Uint8Array(buffer);
};
export const deserializeEntitySnapshots = ({ data, }) => {
    const count = data.length / ENTITY_SNAPSHOT_BYTE_SIZE;
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    const snapshots = [];
    let offset = 0;
    for (let i = 0; i < count; i++) {
        snapshots.push({
            networkId: view.getUint32(offset, true),
            x: view.getFloat32(offset + 4, true),
            y: view.getFloat32(offset + 8, true),
            vx: view.getFloat32(offset + 12, true),
            vy: view.getFloat32(offset + 16, true),
            health: view.getUint16(offset + 20, true),
            state: view.getUint8(offset + 22),
        });
        offset += ENTITY_SNAPSHOT_BYTE_SIZE;
    }
    return snapshots;
};
//# sourceMappingURL=serialization.js.map